import { diffLines } from "diff";

import type { ChangeValue, EntityAttributeChange, Maybe } from "types/generated";
import { EntityChangeType } from "types/generated";
import { isJsonBlob } from "utils/json";

import type {
  RunCodeChangesDiffChunk,
  RunCodeChangesDiffLineType,
  RunCodeChangesGroupNode,
  RunCodeChangesNode,
  RunCodeChangesResolvedValue,
  RunCodeChangesStructuredFormat,
  RunCodeChangesToken,
  RunCodeChangesTokenizedDiffLine,
} from "./types";

/** Grouping requires path segments — API escapes literal dots with backslash (e.g. K8s annotation keys) */
export const splitAttributePath = (path: string): string[] => {
  const parts = path.split(".");
  const result: string[] = [];

  for (let i = 0; i < parts.length; i++) {
    let part = parts[i];

    while (part.endsWith("\\")) {
      i++;
      part += "." + parts[i];
    }

    result.push(part.replaceAll("\\.", "."));
  }

  return result;
};

/** GQL query aliases `value` to stringVal/floatVal/boolVal — runtime has aliased names only */
export const extractAttributeValue = (
  changeValue: Maybe<ChangeValue>
): RunCodeChangesResolvedValue | undefined => {
  if (!changeValue || !changeValue.__typename) return undefined;

  if ("stringVal" in changeValue && typeof changeValue.stringVal === "string") {
    return { type: "string", value: changeValue.stringVal };
  }

  if ("floatVal" in changeValue && typeof changeValue.floatVal === "number") {
    return { type: "number", value: changeValue.floatVal };
  }

  if ("boolVal" in changeValue && typeof changeValue.boolVal === "boolean") {
    return { type: "boolean", value: changeValue.boolVal };
  }

  return undefined;
};

export const buildAttributeTree = (items: EntityAttributeChange[]): RunCodeChangesNode[] => {
  const root: RunCodeChangesNode[] = [];

  for (const item of items) {
    const segments = splitAttributePath(item.path);
    let children = root;

    for (let i = 0; i < segments.length - 1; i++) {
      const key = segments[i];
      const id = segments.slice(0, i + 1).join(".");
      let group = children.find(
        (n): n is RunCodeChangesGroupNode => n.kind === "group" && n.key === key
      );

      if (!group) {
        group = { kind: "group", key, children: [], id };
        children.push(group);
      }

      children = group.children;
    }

    children.push({
      kind: "leaf",
      key: segments[segments.length - 1],
      item,
      id: item.path,
    });
  }

  return root;
};

export const isArrayGroup = (children: RunCodeChangesNode[]): boolean =>
  children.length > 0 && children.every((child) => !isNaN(Number(child.key)));

export const getGroupChangeType = (
  children: RunCodeChangesNode[],
  found?: EntityChangeType
): EntityChangeType | undefined => {
  for (const node of children) {
    const type =
      node.kind === "leaf"
        ? node.item.changeForPhase.metadata.type
        : getGroupChangeType(node.children, found);

    if (!type || type === EntityChangeType.Noop) {
      continue;
    }
    if (!found) {
      found = type;
    }
    if (found !== type) {
      return EntityChangeType.Change;
    }
  }

  return found;
};

const YAML_QUOTED = String.raw`"(?:[^"\\]|\\.)*"|'(?:[^']|'')*'`;
const YAML_KEY = String.raw`[^\s"'#{}\[\],:&*!|>%@][^\s:]*|${YAML_QUOTED}`;
const YAML_KEY_LINE_PATTERN = new RegExp(String.raw`^(?:-[ \t]+)*(${YAML_KEY})[ \t]*:(?:[ \t]|$)`);
const YAML_ENTRY_PATTERN = /^(?:-|---|\.\.\.)(?:[ \t]|$)/;
const YAML_DOCUMENT_START_PATTERN = /^---(?:[ \t]|$)/;

const countLeadingSpaces = (line: string): number => line.search(/[^ ]|$/);

const isYamlBlob = (value: string): boolean => {
  const lines = value
    .replace(/\r\n/g, "\n")
    .split("\n")
    .filter((line) => line.trim() && !line.trimStart().startsWith("#"));

  if (lines.length < 2) return false;

  const baseIndent = countLeadingSpaces(lines[0]);
  const baseKeys = new Set<string>();
  let keyCount = 0;

  for (const line of lines) {
    const indent = countLeadingSpaces(line);

    if (indent < baseIndent) return false;

    const content = line.slice(indent);

    if (YAML_DOCUMENT_START_PATTERN.test(content)) baseKeys.clear();

    const key = content.match(YAML_KEY_LINE_PATTERN)?.[1];

    if (key === undefined) {
      if (indent === baseIndent && !YAML_ENTRY_PATTERN.test(content)) return false;
      continue;
    }

    keyCount++;

    if (indent === baseIndent && !content.startsWith("-")) {
      if (baseKeys.has(key)) return false;
      baseKeys.add(key);
    }
  }

  return keyCount >= 2;
};

export const getStructuredFormat = (
  value: string | undefined
): RunCodeChangesStructuredFormat | undefined => {
  if (!value) return undefined;
  if (isJsonBlob(value)) return "json";
  if (isYamlBlob(value)) return "yaml";

  return undefined;
};

export const prettifyJson = (value: string): string => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const JSON_TOKEN_PATTERN =
  /("(?:[^"\\]|\\.)*")\s*(:?)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\b|\b(true|false)\b|\b(null)\b/g;

export const tokenizeJsonLine = (line: string): RunCodeChangesToken[] => {
  const tokens: RunCodeChangesToken[] = [];
  let lastIndex = 0;

  for (const match of line.matchAll(JSON_TOKEN_PATTERN)) {
    const index = match.index ?? 0;

    if (index > lastIndex) {
      tokens.push({ text: line.slice(lastIndex, index), type: "punctuation" });
    }

    if (match[1] !== undefined) {
      const isKey = match[2] === ":";
      tokens.push({ text: match[1], type: isKey ? "key" : "string" });

      if (isKey) {
        tokens.push({ text: ":", type: "punctuation" });
      }
    } else if (match[3] !== undefined) {
      tokens.push({ text: match[0], type: "number" });
    } else if (match[4] !== undefined) {
      tokens.push({ text: match[0], type: "boolean" });
    } else if (match[5] !== undefined) {
      tokens.push({ text: match[0], type: "null" });
    }

    lastIndex = index + match[0].length;
  }

  if (lastIndex < line.length) {
    tokens.push({ text: line.slice(lastIndex), type: "punctuation" });
  }

  return tokens;
};

const YAML_PREFIX_PATTERN = /^[ \t]*(?:-(?:[ \t]+|$))*/;
const YAML_KEY_PATTERN = new RegExp(String.raw`^(${YAML_KEY})([ \t]*:)(?=[ \t]|$)`);
const YAML_QUOTED_PATTERN = new RegExp(`^(?:${YAML_QUOTED})`);
const YAML_COMMENT_PATTERN = /(?:^|\s)#/;
const YAML_NUMBER_PATTERN = /^[-+]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][-+]?\d+)?$/;
const YAML_BOOLEAN_PATTERN = /^(?:true|false)$/i;
const YAML_NULL_PATTERN = /^(?:null|~)$/i;
const YAML_BLOCK_INDICATOR_PATTERN = /^(?:[!&]\S*[ \t]+)*[|>][-+0-9]*$/;
const YAML_INDICATOR_PATTERN = /^(?:[|>][-+0-9]*$|[[{]|---$|\.\.\.$)/;

const getYamlScalarType = (scalar: string): RunCodeChangesToken["type"] => {
  if (YAML_NUMBER_PATTERN.test(scalar)) return "number";
  if (YAML_BOOLEAN_PATTERN.test(scalar)) return "boolean";
  if (YAML_NULL_PATTERN.test(scalar)) return "null";
  if (YAML_INDICATOR_PATTERN.test(scalar)) return "punctuation";

  return "string";
};

const tokenizeYamlLine = (line: string) => {
  const tokens: RunCodeChangesToken[] = [];
  const push = (text: string, type: RunCodeChangesToken["type"]) => {
    if (text) tokens.push({ text, type });
  };

  const prefix = line.match(YAML_PREFIX_PATTERN)?.[0] ?? "";
  const keyMatch = line.slice(prefix.length).match(YAML_KEY_PATTERN);
  const rest = line.slice(prefix.length + (keyMatch?.[0].length ?? 0));
  const value = rest.trimStart();
  const scalarEnd =
    value.match(YAML_QUOTED_PATTERN)?.[0].length ??
    value.match(YAML_COMMENT_PATTERN)?.index ??
    value.length;
  const scalar = value.slice(0, scalarEnd).trimEnd();

  push(prefix, "punctuation");
  push(keyMatch?.[1] ?? "", "key");
  push(keyMatch?.[2] ?? "", "punctuation");
  push(rest.slice(0, rest.length - value.length), "punctuation");
  push(scalar, getYamlScalarType(scalar));
  push(value.slice(scalar.length), "punctuation");

  let blockParentColumn: number | undefined;

  if (YAML_BLOCK_INDICATOR_PATTERN.test(scalar)) {
    blockParentColumn = keyMatch || !prefix.includes("-") ? prefix.length : prefix.lastIndexOf("-");
  }

  return { tokens, blockParentColumn };
};

export const tokenizeYamlLines = (text: string): RunCodeChangesToken[][] => {
  let blockParentColumn: number | undefined;

  return text.split("\n").map((line): RunCodeChangesToken[] => {
    const isBlockBody =
      blockParentColumn !== undefined &&
      (!line.trim() || countLeadingSpaces(line) > blockParentColumn);

    if (isBlockBody) return line ? [{ text: line, type: "string" }] : [];

    const result = tokenizeYamlLine(line);
    blockParentColumn = result.blockParentColumn;

    return result.tokens;
  });
};

const formatStructuredValue = (
  value: string | undefined,
  format: RunCodeChangesStructuredFormat
): string => {
  if (!value) return "";
  if (format === "json") return prettifyJson(value);

  return value.replace(/\r\n/g, "\n").trimEnd();
};

const tokenizeStructuredValue = (
  text: string,
  format: RunCodeChangesStructuredFormat
): RunCodeChangesToken[][] =>
  format === "json"
    ? text.split("\n").map((line) => tokenizeJsonLine(line))
    : tokenizeYamlLines(text);

const toDiffLines = (
  lines: RunCodeChangesToken[][],
  type: RunCodeChangesDiffLineType
): RunCodeChangesTokenizedDiffLine[] => lines.map((tokens) => ({ tokens, type }));

export const diffStructuredValues = (
  before: string | undefined,
  after: string | undefined,
  format: RunCodeChangesStructuredFormat
): RunCodeChangesTokenizedDiffLine[] => {
  const beforeText = formatStructuredValue(before, format);
  const afterText = formatStructuredValue(after, format);

  if (!beforeText) return toDiffLines(tokenizeStructuredValue(afterText, format), "added");
  // Known after apply has no after value, so every before line shows as removed
  if (!afterText) return toDiffLines(tokenizeStructuredValue(beforeText, format), "removed");

  const beforeLines = tokenizeStructuredValue(beforeText, format);
  const afterLines = tokenizeStructuredValue(afterText, format);
  let beforeIndex = 0;
  let afterIndex = 0;

  // Every line ends with a newline, so the last line compares like the others
  return diffLines(`${beforeText}\n`, `${afterText}\n`).flatMap((change) => {
    if (change.removed) {
      beforeIndex += change.count;
      return toDiffLines(beforeLines.slice(beforeIndex - change.count, beforeIndex), "removed");
    }

    afterIndex += change.count;
    const lines = afterLines.slice(afterIndex - change.count, afterIndex);

    if (change.added) return toDiffLines(lines, "added");

    beforeIndex += change.count;
    return toDiffLines(lines, "unchanged");
  });
};

const MIN_FOLDED_LINES = 4;

export const collapseUnchangedLines = (
  lines: RunCodeChangesTokenizedDiffLine[],
  context = 3
): RunCodeChangesDiffChunk[] => {
  const chunks: RunCodeChangesDiffChunk[] = [];
  let index = 0;

  while (index < lines.length) {
    if (lines[index].type !== "unchanged") {
      chunks.push(lines[index]);
      index++;
      continue;
    }

    let end = index;
    while (end < lines.length && lines[end].type === "unchanged") end++;

    const foldStart = index + (index === 0 ? 0 : context);
    const foldEnd = end - (end === lines.length ? 0 : context);

    if (foldEnd - foldStart < MIN_FOLDED_LINES) {
      chunks.push(...lines.slice(index, end));
    } else {
      chunks.push(
        ...lines.slice(index, foldStart),
        { count: foldEnd - foldStart, lines: lines.slice(foldStart, foldEnd) },
        ...lines.slice(foldEnd, end)
      );
    }

    index = end;
  }

  return chunks;
};
